/**
 * Billing Service
 * Handles all billing-related API calls and business logic
 * Uses server endpoints exclusively (no Parse cloud functions)
 * 
 * Note: For formatting, colors, and calculations, use BillingUtils instead
 */

import { getServerBaseUrl } from '@/utils/serverUrl'
import { getStoredSessionToken } from '../utils/sessionToken'
import { withBearerAuth } from '../utils/authHeaders'

export interface BillingTier {
  id: string
  name: string
  tier_key: string
  description: string
  base_price: number
  price_per_user: number
  free_users_limit: number
  max_users: number
  max_rooms: number
  features: Record<string, any>
  stripe_price_id?: string
  is_active: boolean
  color?: string
  is_default?: boolean
  requires_sales_contact: boolean
  sales_contact_message?: string
  created_at: string
  updated_at: string
}

export interface CompanyBillingAccount {
  id: string
  company_id: string
  billing_tier_id: string
  status: 'pending' | 'active' | 'suspended' | 'cancelled'
  payment_status: 'unpaid' | 'paid' | 'overdue' | 'pending'
  stripe_customer_id?: string
  stripe_subscription_id?: string
  payment_method_id?: string
  billing_cycle: 'monthly' | 'yearly'
  next_billing_date?: string
  last_payment_date?: string
  last_payment_amount: number
  total_paid: number
  outstanding_balance: number
  created_at: string
  updated_at: string
  billing_tier?: BillingTier
}

export interface PaymentMethod {
  id: string
  company_id: string
  type: string
  is_default: boolean
  status: string
  created_at: string
  updated_at: string
  card?: {
    brand: string
    last4: string
    exp_month: number
    exp_year: number
  }
  us_bank_account?: {
    account_holder_type: string
    account_type: string
    bank_name?: string
    last4: string
    routing_number?: string
  }
}

export interface BillingHistory {
  id: string
  company_id: string
  amount: number
  status: 'pending' | 'paid' | 'failed' | 'refunded'
  stripe_invoice_id?: string
  description: string
  billing_date: string
  created_at: string
}

export interface BillingCalculation {
  company_id: string
  company_name: string
  billing_tier: {
    id: string
    name: string
    tier_key: string
  }
  base_price: number
  active_users: number
  free_users_limit: number
  billable_users: number
  price_per_user: number
  user_cost: number
  total_cost: number
  billing_cycle: string
  next_billing_date?: string
  payment_status: string
  outstanding_balance: number
}


export interface StripeSubscription {
  id: string
  status: string
  current_period_start: number
  current_period_end: number
  items: {
    data: Array<{
      price: {
        id: string
        unit_amount: number
        currency: string
        recurring: {
          interval: string
        }
      }
    }>
  }
}

export interface StripeInvoice {
  id: string
  amount_paid: number
  amount_due: number
  currency: string
  status: string
  created: number
  period_start: number
  period_end: number
  subscription?: string
}

class BillingService {
  private serverBaseUrl: string
  /** In-flight dedupe: same company + query flags share one fetch (rapid tab/dialog switches). */
  private comprehensiveInFlight = new Map<string, Promise<{
    success: boolean
    billing_account?: CompanyBillingAccount
    billing_calculation?: BillingCalculation
    payment_methods?: PaymentMethod[]
    billing_history?: BillingHistory[]
    upcoming_invoice?: any
    stripe_config?: any
    subscriptions?: any[]
    error?: string
  }>>()

  constructor() {
    this.serverBaseUrl = getServerBaseUrl()
  }

  private comprehensiveCacheKey(
    companyId: string,
    options: { include_history?: boolean; history_limit?: number; include_upcoming?: boolean }
  ): string {
    return [
      companyId,
      options.include_history ?? 'u',
      options.history_limit ?? 'u',
      options.include_upcoming ?? 'u',
    ].join(':')
  }

  /**
   * Get all billing tiers
   */
  async getBillingTiers(activeOnly: boolean = true): Promise<{ success: boolean; billing_tiers: BillingTier[] }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-tiers?active_only=${activeOnly}`)
      const data = await response.json()
      return { success: response.ok, billing_tiers: data.billing_tiers || [] }
    } catch (error) {
      console.error('Error fetching billing tiers:', error)
      throw error
    }
  }

  /**
   * Sync company with Stripe customer
   * Creates or updates Stripe customer and billing account
   */
  async syncCompanyWithStripe(companyData: {
    company_id: string
    company_name?: string
    company_email?: string
    user_id?: string
    user_name?: string
    user_email?: string
  }): Promise<{ success: boolean; customer?: any; billing_account?: any; error?: string }> {
    try {
      // Get the current user count for the company
      const userCount = await this.getCompanyUserCount(companyData.company_id)

      const response = await fetch(`${this.serverBaseUrl}/api/stripe/customer/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...companyData,
          user_count: userCount
        })
      })

      const data = await response.json()

      if (data.success) {
        return {
          success: true,
          customer: data.customer,
          billing_account: data.billing_account
        }
      } else {
        return { success: false, error: data.error || 'Failed to sync with Stripe' }
      }
    } catch (error) {
      console.error('Error syncing company with Stripe:', error)
      return { success: false, error: 'Failed to sync with Stripe' }
    }
  }

  /**
   * Get company billing account
   * Gets real billing account data from the database
   */
  async getCompanyBillingAccount(companyId: string): Promise<{ success: boolean; billing_account?: CompanyBillingAccount; message?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-account/${companyId}`)
      const data = await response.json()

      if (data.success) {
        // If no billing account exists, return null instead of creating a mock one
        if (!data.billing_account) {
          return {
            success: true,
            billing_account: null,
            message: 'No billing account configured for this company'
          }
        }

        return {
          success: true,
          billing_account: data.billing_account,
          message: data.message
        }
      } else {
        return { success: false, message: data.error || 'Failed to fetch billing account' }
      }
    } catch (error) {
      console.error('Error fetching company billing account:', error)
      return { success: false, message: 'Failed to fetch billing account' }
    }
  }

  /**
   * Create company billing account
   */
  async createCompanyBillingAccount(companyId: string, tierId: string): Promise<{ success: boolean; billing_account?: CompanyBillingAccount; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          billing_tier_id: tierId
        })
      })
      const data = await response.json()

      if (data.success) {
        return { success: true, billing_account: data.billing_account }
      } else {
        return { success: false, error: data.error || 'Failed to create billing account' }
      }
    } catch (error: any) {
      console.error('Error creating company billing account:', error)
      return { success: false, error: error.message || 'Failed to create billing account' }
    }
  }

  /**
   * Update company billing account
   */
  async updateCompanyBillingAccount(companyId: string, updates: Partial<CompanyBillingAccount>): Promise<{ success: boolean; billing_account?: CompanyBillingAccount; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-account/${companyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      })
      const data = await response.json()

      if (data.success) {
        return { success: true, billing_account: data.billing_account }
      } else {
        return { success: false, error: data.error || 'Failed to update billing account' }
      }
    } catch (error: any) {
      console.error('Error updating company billing account:', error)
      return { success: false, error: error.message || 'Failed to update billing account' }
    }
  }

  /**
   * Check if a billing tier is completely free (no base cost and no per-user cost)
   * @deprecated Use BillingUtils.isFreeTier instead
   */
  isFreeTier(tier: BillingTier): boolean {
    return tier.base_price === 0 && tier.price_per_user === 0
  }

  /**
   * Check if a billing calculation represents a free tier
   * @deprecated Use BillingUtils.isFreeTierCalculation instead
   */
  isFreeTierCalculation(calculation: BillingCalculation): boolean {
    return calculation.base_price === 0 && calculation.price_per_user === 0
  }

  /**
   * Calculate company billing
   * This will be calculated client-side based on tier and user count
   */
  async calculateCompanyBilling(companyId: string): Promise<{ success: boolean; billing: BillingCalculation }> {
    try {
      // Get billing account, tiers, and company users
      const [accountResult, tiersResult, companyUsersResult] = await Promise.all([
        this.getCompanyBillingAccount(companyId),
        this.getBillingTiers(),
        this.getCompanyUserCount(companyId)
      ])

      if (!accountResult.success || !tiersResult.success) {
        throw new Error('Failed to fetch billing data')
      }

      const billingAccount = accountResult.billing_account
      const tiers = tiersResult.billing_tiers
      const activeUsers = companyUsersResult.success ? companyUsersResult.userCount : 0

      // Find the current tier
      const currentTier = tiers.find(tier => tier.id === billingAccount?.billing_tier_id) || tiers[0]

      const billableUsers = Math.max(0, activeUsers - currentTier.free_users_limit)
      const userCost = billableUsers * currentTier.price_per_user
      const totalCost = currentTier.base_price + userCost

      const billing: BillingCalculation = {
        company_id: companyId,
        company_name: `Company ${companyId}`, // This should be fetched from company data
        billing_tier: {
          id: currentTier.id,
          name: currentTier.name,
          tier_key: currentTier.tier_key
        },
        base_price: currentTier.base_price,
        active_users: activeUsers,
        free_users_limit: currentTier.free_users_limit,
        billable_users: billableUsers,
        price_per_user: currentTier.price_per_user,
        user_cost: userCost,
        total_cost: totalCost,
        billing_cycle: billingAccount?.billing_cycle || 'monthly',
        next_billing_date: billingAccount?.next_billing_date,
        payment_status: billingAccount?.payment_status || 'unpaid',
        outstanding_balance: billingAccount?.outstanding_balance || 0
      }

      return { success: true, billing }
    } catch (error) {
      console.error('Error calculating company billing:', error)
      throw error
    }
  }

  /**
   * Get company payment methods
   */
  async getCompanyPaymentMethods(companyId: string): Promise<{ success: boolean; payment_methods: PaymentMethod[] }> {
    try {
      // First get the billing account to find the Stripe customer ID
      const billingAccountResult = await this.getCompanyBillingAccount(companyId)
      if (!billingAccountResult.success || !billingAccountResult.billing_account?.stripe_customer_id) {
        return { success: true, payment_methods: [] }
      }

      // Get payment methods from Stripe via server (fetch all types)
      const result = await this.getPaymentMethods(billingAccountResult.billing_account.stripe_customer_id, 'all')
      if (result.success && result.data?.payment_methods) {
        // Debug: Log raw Stripe data to understand what we're getting
        console.log('Raw Stripe payment methods data:', result.data.payment_methods)
        // Transform Stripe payment methods to our format
        const paymentMethods: PaymentMethod[] = result.data.payment_methods.map((pm: any) => {
          // Determine status based on Stripe's actual status and data availability
          let status = 'inactive'

          console.log(`Processing payment method ${pm.id} of type ${pm.type}`)

          console.log(`Processing payment method ${pm.id}:`, {
            type: pm.type,
            card: pm.card,
            us_bank_account: pm.us_bank_account,
            status_details: pm.us_bank_account?.status_details
          })

          if (pm.type === 'card') {
            // For cards, check if we have valid card data and it's not expired
            if (pm.card && pm.card.last4) {
              console.log(`Card data for ${pm.id}:`, {
                last4: pm.card.last4,
                exp_year: pm.card.exp_year,
                exp_month: pm.card.exp_month,
                brand: pm.card.brand
              })

              // Check if card is expired
              const currentDate = new Date()
              const currentYear = currentDate.getFullYear()
              const currentMonth = currentDate.getMonth() + 1 // getMonth() returns 0-11
              const expYear = pm.card.exp_year
              const expMonth = pm.card.exp_month

              console.log(`Expiration check:`, {
                currentYear,
                currentMonth,
                expYear,
                expMonth
              })

              // Card is expired if exp_year < currentYear OR (exp_year === currentYear AND exp_month < currentMonth)
              const isExpired = expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)

              console.log(`Card ${pm.id} is expired:`, isExpired)

              if (isExpired) {
                status = 'expired'
              } else {
                status = 'active'
              }
            } else {
              console.log(`Card ${pm.id} missing data:`, {
                hasCard: !!pm.card,
                hasLast4: pm.card?.last4
              })
              status = 'inactive'
            }
          } else if (pm.type === 'us_bank_account') {
            // For bank accounts, check Stripe's verification status and blocked status
            if (pm.us_bank_account) {
              console.log(`Bank account status: ${pm.us_bank_account.status}`)
              console.log(`Bank account blocked:`, pm.us_bank_account.status_details?.blocked)

              // Check if account is blocked first
              if (pm.us_bank_account.status_details?.blocked) {
                status = 'blocked'
              } else if (pm.us_bank_account.status === 'verified') {
                status = 'active'
              } else if (pm.us_bank_account.status === 'pending_verification' || pm.us_bank_account.status === 'new') {
                status = 'pending'
              } else {
                status = 'inactive'
              }
            }
          }

          // Final fallback: if it's a card with valid data but still inactive, make it active
          if (pm.type === 'card' && status === 'inactive' && pm.card && pm.card.last4) {
            console.log(`Fallback: Card ${pm.id} has valid data but was marked inactive, setting to active`)
            status = 'active'
          }

          console.log(`Final status for ${pm.id}: ${status}`)

          return {
            id: pm.id,
            company_id: companyId,
            type: pm.type,
            is_default: pm.id === result.data.customer?.invoice_settings?.default_payment_method,
            status: status,
            created_at: pm.created ? new Date(pm.created * 1000).toISOString() : new Date().toISOString(),
            updated_at: pm.created ? new Date(pm.created * 1000).toISOString() : new Date().toISOString(),
            card: pm.card ? {
              brand: pm.card.brand,
              last4: pm.card.last4,
              exp_month: pm.card.exp_month,
              exp_year: pm.card.exp_year
            } : undefined,
            us_bank_account: pm.us_bank_account ? {
              account_holder_type: pm.us_bank_account.account_holder_type,
              account_type: pm.us_bank_account.account_type,
              bank_name: pm.us_bank_account.bank_name,
              last4: pm.us_bank_account.last4,
              routing_number: pm.us_bank_account.routing_number
            } : undefined
          }
        })
        return { success: true, payment_methods: paymentMethods }
      }

      return { success: true, payment_methods: [] }
    } catch (error) {
      console.error('Error fetching company payment methods:', error)
      throw error
    }
  }

  /**
   * Get company billing history
   */
  async getCompanyBillingHistory(companyId: string, limit: number = 50, skip: number = 0): Promise<{ success: boolean; billing_history: BillingHistory[]; total: number }> {
    try {
      // First get the billing account to find the Stripe customer ID
      const billingAccountResult = await this.getCompanyBillingAccount(companyId)
      if (!billingAccountResult.success || !billingAccountResult.billing_account?.stripe_customer_id) {
        return { success: true, billing_history: [], total: 0 }
      }

      // Get invoices from Stripe via server
      const result = await this.getInvoices(billingAccountResult.billing_account.stripe_customer_id, { limit })
      if (result.success && result.data?.invoices) {
        // Transform Stripe invoices to our billing history format
        const billingHistory: BillingHistory[] = result.data.invoices.map((invoice: any) => ({
          id: invoice.id,
          company_id: companyId,
          amount: invoice.amount_paid / 100, // Convert from cents
          status: invoice.status === 'paid' ? 'paid' :
            invoice.status === 'open' ? 'pending' :
              invoice.status === 'void' ? 'failed' : 'pending',
          stripe_invoice_id: invoice.id,
          description: invoice.description || `Invoice for ${new Date(invoice.created * 1000).toLocaleDateString()}`,
          billing_date: new Date(invoice.created * 1000).toISOString(),
          created_at: new Date(invoice.created * 1000).toISOString()
        }))
        return { success: true, billing_history: billingHistory, total: billingHistory.length }
      }

      return { success: true, billing_history: [], total: 0 }
    } catch (error) {
      console.error('Error fetching company billing history:', error)
      throw error
    }
  }

  /**
   * Format currency for display
   * @deprecated Use BillingUtils.formatCurrency instead
   */
  formatCurrency(amount: number, currency: string = 'USD'): string {
    // Import BillingUtils dynamically to avoid circular dependencies
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  /**
   * Get billing status color
   * @deprecated Use BillingUtils.getBillingStatusColor instead
   */
  getBillingStatusColor(status: string): string {
    switch (status) {
      case 'paid':
        return '#4caf50'
      case 'pending':
        return '#ff9800'
      case 'overdue':
        return '#f44336'
      case 'unpaid':
        return '#9e9e9e'
      case 'nothing':
        return '#4caf50'
      default:
        return '#9e9e9e'
    }
  }

  /**
   * Get billing status label
   * @deprecated Use BillingUtils.getBillingStatusLabel instead
   */
  getBillingStatusLabel(status: string): string {
    switch (status) {
      case 'paid':
        return 'Paid'
      case 'pending':
        return 'Pending'
      case 'overdue':
        return 'Overdue'
      case 'unpaid':
        return 'Unpaid'
      case 'nothing':
        return 'Nothing'
      default:
        return 'Unknown'
    }
  }



  /**
   * Get company user count
   */
  async getCompanyUserCount(companyId: string): Promise<{ success: boolean; userCount: number; error?: string }> {
    try {
      // Import CompanyService dynamically to avoid circular dependencies
      const { CompanyService } = await import('./CompanyService')
      // Single lightweight page: cloud returns authoritative `total` for the full roster
      const result = await CompanyService.getCompanyUsers(companyId, undefined, true, 1, 1)

      if (result.success && result.users !== undefined) {
        const userCount = result.total ?? result.users.length
        return { success: true, userCount }
      } else {
        console.error('Failed to get company users:', result.error)
        return { success: false, userCount: 0, error: result.error }
      }
    } catch (error: any) {
      console.error('Error getting company user count:', error)
      return { success: false, userCount: 0, error: error.message }
    }
  }

  // ============================================================================
  // SUBSCRIPTION PURCHASE & PAYMENT METHODS
  // ============================================================================

  /**
   * Create subscription checkout session
   */
  async createSubscriptionCheckout(params: {
    tier_id: string
    billing_cycle?: 'monthly' | 'yearly'
    user_count?: number
    company_id: string
    user_id: string
    success_url?: string
    cancel_url?: string
    customer_email?: string
    customer_name?: string
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/subscriptions/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      const data = await response.json()

      // Extract error message from response if request failed
      if (!response.ok) {
        const errorMessage = data?.error || data?.data?.error || 'Failed to create checkout session'
        return { success: false, data, error: errorMessage }
      }

      return { success: true, data }
    } catch (error: any) {
      console.error('Error creating subscription checkout:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Create subscription directly with existing payment method
   */
  async createSubscription(params: {
    tier_id: string
    billing_cycle?: 'monthly' | 'yearly'
    user_count?: number
    company_id: string
    user_id: string
    customer_id: string
    payment_method_id?: string
    prorate?: boolean
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/subscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error creating subscription:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get company subscriptions
   */
  async getCompanySubscriptions(companyId: string, includeInactive: boolean = false): Promise<{ success: boolean; subscriptions?: any[]; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/subscriptions/${companyId}?includeInactive=${includeInactive}`)
      const data = await response.json()
      return { success: response.ok, subscriptions: data.subscriptions, error: data.error }
    } catch (error: any) {
      console.error('Error getting company subscriptions:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Update subscription
   */
  async updateSubscription(subscriptionId: string, params: {
    tier_id?: string
    user_count?: number
    billing_cycle?: 'monthly' | 'yearly'
    prorate?: boolean
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/subscriptions/${subscriptionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error updating subscription:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(subscriptionId: string, params: {
    cancel_at_period_end?: boolean
    cancellation_reason?: string
  } = {}): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error canceling subscription:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Reactivate subscription
   */
  async reactivateSubscription(subscriptionId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/subscriptions/${subscriptionId}/reactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error reactivating subscription:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Toggle auto-renew for a subscription
   */
  async toggleAutoRenew(subscriptionId: string, autoRenew: boolean): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/subscriptions/${subscriptionId}/toggle-auto-renew`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_renew: autoRenew })
      })
      const data = await response.json()
      return { success: response.ok, data, error: data.error }
    } catch (error: any) {
      console.error('Error toggling auto-renew:', error)
      return { success: false, error: error.message }
    }
  }

  async syncSubscriptions(companyId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/sync-subscriptions/${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      return { success: response.ok, message: data.message, error: data.error }
    } catch (error: any) {
      console.error('Error syncing subscriptions:', error)
      return { success: false, error: error.message }
    }
  }


  // ============================================================================
  // PAYMENT METHODS MANAGEMENT
  // ============================================================================

  /**
   * Get payment methods for a customer
   */
  async getPaymentMethods(customerId: string, type: string = 'card'): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/payment-methods/${customerId}?type=${type}`)
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error getting payment methods:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Create setup intent for payment method
   */
  async createSetupIntent(params: {
    customer_id: string
    return_url?: string
    usage?: 'off_session' | 'on_session'
    payment_method_types?: string[]
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/payment-methods/setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error creating setup intent:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get comprehensive billing data for a company in one call
   * This replaces multiple individual API calls to reduce rate limiting
   */
  async getComprehensiveBillingData(companyId: string, options: {
    include_history?: boolean
    history_limit?: number
    include_upcoming?: boolean
    include_payment_methods?: boolean
    include_subscriptions?: boolean
  } = {}): Promise<{
    success: boolean
    billing_account?: CompanyBillingAccount
    billing_calculation?: BillingCalculation
    payment_methods?: PaymentMethod[]
    billing_history?: BillingHistory[]
    upcoming_invoice?: any
    stripe_config?: any
    subscriptions?: any[]
    error?: string
  }> {
    const key = this.comprehensiveCacheKey(companyId, options)
    const existing = this.comprehensiveInFlight.get(key)
    if (existing) {
      return existing
    }

    const promise = this.fetchComprehensiveBillingDataOnce(companyId, options)
    this.comprehensiveInFlight.set(key, promise)
    try {
      return await promise
    } finally {
      this.comprehensiveInFlight.delete(key)
    }
  }

  private async fetchComprehensiveBillingDataOnce(companyId: string, options: {
    include_history?: boolean
    history_limit?: number
    include_upcoming?: boolean
    include_payment_methods?: boolean
    include_subscriptions?: boolean
  }): Promise<{
    success: boolean
    billing_account?: CompanyBillingAccount
    billing_calculation?: BillingCalculation
    payment_methods?: PaymentMethod[]
    billing_history?: BillingHistory[]
    upcoming_invoice?: any
    stripe_config?: any
    subscriptions?: any[]
    error?: string
  }> {
    try {
      const params = new URLSearchParams()
      if (options.include_history !== undefined) {
        params.append('include_history', options.include_history.toString())
      }
      if (options.history_limit !== undefined) {
        params.append('history_limit', options.history_limit.toString())
      }
      if (options.include_upcoming !== undefined) {
        params.append('include_upcoming', options.include_upcoming.toString())
      }
      if (options.include_payment_methods !== undefined) {
        params.append('include_payment_methods', options.include_payment_methods.toString())
      }
      if (options.include_subscriptions !== undefined) {
        params.append('include_subscriptions', options.include_subscriptions.toString())
      }

      const queryString = params.toString()
      const url = `${this.serverBaseUrl}/api/stripe/billing-comprehensive/${companyId}${queryString ? '?' + queryString : ''}`

      const token = getStoredSessionToken()
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      })
      const data = await response.json()

      return {
        success: response.ok,
        billing_account: data.billing_account,
        billing_calculation: data.billing_calculation,
        payment_methods: data.payment_methods,
        billing_history: data.billing_history,
        upcoming_invoice: data.upcoming_invoice,
        stripe_config: data.stripe_config,
        subscriptions: data.subscriptions,
        error: data.error,
      }
    } catch (error: any) {
      console.error('Error getting comprehensive billing data:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Create payment method using setup intent
   * This creates a setup intent that can be used to collect payment method details
   */
  async createPaymentMethod(companyId: string, paymentMethodData: any, paymentMethodType: 'card' | 'us_bank_account' = 'card'): Promise<{ success: boolean; setup_intent?: any; error?: string }> {
    try {
      // First, get the company billing account to find the Stripe customer ID
      const accountResult = await this.getCompanyBillingAccount(companyId)

      if (!accountResult.success) {
        console.error('Failed to get billing account:', accountResult)
        return { success: false, error: 'Failed to get billing account: ' + (accountResult.message || 'Unknown error') }
      }

      if (!accountResult.billing_account) {
        console.log('No billing account found for company, creating one...')

        // Try to create a billing account first - get the starter tier ID
        const tiersResult = await this.getBillingTiers(true) // Get active tiers
        if (!tiersResult.success || !tiersResult.billing_tiers.length) {
          console.error('Failed to get billing tiers:', tiersResult)
          return { success: false, error: 'Failed to get billing tiers to create account' }
        }

        // Find the starter tier (or use the first tier as default)
        const starterTier = tiersResult.billing_tiers.find(tier => tier.tier_key === 'starter') || tiersResult.billing_tiers[0]
        if (!starterTier) {
          console.error('No billing tiers available')
          return { success: false, error: 'No billing tiers available' }
        }

        console.log('Creating billing account with tier:', starterTier.tier_key, starterTier.id)
        const createAccountResult = await this.createCompanyBillingAccount(companyId, starterTier.id)
        if (!createAccountResult.success) {
          console.error('Failed to create billing account:', createAccountResult)
          return { success: false, error: 'Failed to create billing account: ' + (createAccountResult.error || 'Unknown error') }
        }

        console.log('Billing account created, retrying fetch...')

        // Retry getting the billing account
        const retryAccountResult = await this.getCompanyBillingAccount(companyId)
        if (!retryAccountResult.success || !retryAccountResult.billing_account) {
          console.error('Still no billing account after creation:', retryAccountResult)
          return { success: false, error: 'Failed to create billing account for company' }
        }

        // Update the account result with the retry
        accountResult.billing_account = retryAccountResult.billing_account
      }

      if (!accountResult.billing_account.stripe_customer_id) {
        console.log('No Stripe customer ID found, attempting to sync company with Stripe...')

        // Try to sync the company with Stripe first
        const syncResult = await this.syncCompanyWithStripe({
          company_id: companyId,
          company_name: `Company ${companyId}`, // This should ideally come from company data
          company_email: '', // This should ideally come from company data
          user_id: '', // This should come from auth context
          user_name: '', // This should come from auth context
          user_email: '' // This should come from auth context
        })

        if (!syncResult.success) {
          console.error('Failed to sync company with Stripe:', syncResult.error)
          return { success: false, error: 'Failed to sync company with Stripe: ' + (syncResult.error || 'Unknown error') }
        }

        console.log('Company synced with Stripe, retrying billing account fetch...')

        // Retry getting the billing account
        const retryAccountResult = await this.getCompanyBillingAccount(companyId)
        if (!retryAccountResult.success || !retryAccountResult.billing_account?.stripe_customer_id) {
          console.error('Still no Stripe customer ID after sync:', retryAccountResult)
          return { success: false, error: 'Failed to create Stripe customer for company' }
        }

        // Update the account result with the retry
        accountResult.billing_account = retryAccountResult.billing_account
      }

      const customerId = accountResult.billing_account.stripe_customer_id
      console.log('Using Stripe customer ID:', customerId)

      // Create setup intent
      const setupIntentParams = {
        customer_id: customerId,
        usage: 'off_session' as 'off_session',
        payment_method_types: [paymentMethodType]
        // Note: return_url not needed for setup intents that redirect to Stripe's hosted page
      }
      console.log('Creating setup intent with params:', setupIntentParams)

      const result = await this.createSetupIntent(setupIntentParams)
      console.log('Setup intent result:', result)
      console.log('Setup intent result data:', result.data)

      if (result.success && result.data?.setup_intent) {
        const setupIntent = result.data.setup_intent
        console.log('Setup intent object:', setupIntent)

        // Return the setup intent with client_secret for Stripe Elements
        return {
          success: true,
          setup_intent: setupIntent
        }
      } else {
        console.error('Setup intent creation failed:', result)
        console.error('Setup intent error details:', result.data)
        return { success: false, error: result.error || result.data?.error || 'Failed to create setup intent' }
      }
    } catch (error: any) {
      console.error('Error creating payment method:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Set default payment method
   */
  async setDefaultPaymentMethod(paymentMethodId: string, customerId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/payment-methods/${paymentMethodId}/set-default`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId })
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error setting default payment method:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Delete payment method
   */
  async deletePaymentMethod(paymentMethodId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/payment-methods/${paymentMethodId}`, {
        method: 'DELETE'
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error deleting payment method:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================================================
  // CUSTOMER PORTAL
  // ============================================================================

  /**
   * Create customer portal session
   */
  async createCustomerPortalSession(params: {
    customer_id: string
    return_url?: string
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/customer-portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error creating customer portal session:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================================================
  // INVOICE MANAGEMENT
  // ============================================================================

  /**
   * Get invoices for a customer
   */
  async getInvoices(customerId: string, params: {
    limit?: number
    starting_after?: string
    ending_before?: string
  } = {}): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const queryParams = new URLSearchParams()
      if (params.limit) queryParams.append('limit', params.limit.toString())
      if (params.starting_after) queryParams.append('starting_after', params.starting_after)
      if (params.ending_before) queryParams.append('ending_before', params.ending_before)

      const response = await fetch(`${this.serverBaseUrl}/api/stripe/invoices/${customerId}?${queryParams}`)
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error getting invoices:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get upcoming invoice for a customer
   */
  async getUpcomingInvoice(customerId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/invoices/${customerId}/upcoming`)
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error getting upcoming invoice:', error)
      return { success: false, error: error.message }
    }
  }


  // ============================================================================
  // STRIPE CONFIGURATION & ADMIN METHODS
  // ============================================================================

  /**
   * Get Stripe configuration
   */
  async getStripeConfig(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      // Abort the request if the server doesn't respond within 10 seconds.
      // Without this, on a slow/unreachable server the fetch hangs forever and
      // the StripeProvider loading spinner never resolves.
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)
      try {
        const response = await fetch(`${this.serverBaseUrl}/api/stripe/config`, {
          signal: controller.signal,
        })
        const data = await response.json()
        return { success: response.ok, data }
      } finally {
        clearTimeout(timeoutId)
      }
    } catch (error: any) {
      console.error('Error getting Stripe config:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Sync billing tiers with Stripe catalog (server-first approach)
   */
  async syncStripeCatalog(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/sync-catalog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error syncing Stripe catalog:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Force sync all billing tiers to Stripe (server-first approach)
   * This ensures server data completely overwrites Stripe data
   */
  async forceSyncAllTiersToStripe(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/force-sync-all-tiers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error force syncing all tiers to Stripe:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get subscription counts
   */
  async getSubscriptionCounts(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/subscription-counts`)
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error fetching subscription counts:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Sync individual tier
   */
  async syncIndividualTier(tierId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/sync-tier/${tierId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error syncing individual tier:', error)
      return { success: false, error: error.message }
    }
  }


  // ============================================================================
  // TEST DATA MANAGEMENT
  // ============================================================================

  /**
   * Create test customers
   */
  async createTestCustomers(count: number = 100): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/create-test-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count })
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error creating test customers:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Cleanup test customers
   */
  async cleanupTestCustomers(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/cleanup-test-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error cleaning up test customers:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get test data statistics
   */
  async getTestDataStats(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/test-data-stats`)
      const data = await response.json()
      return { success: response.ok, data }
    } catch (error: any) {
      console.error('Error getting test data stats:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================================================
  // MISSING METHODS FOR ADMIN BILLING SETTINGS
  // ============================================================================

  /**
   * Get billing tier migration status
   */
  async getBillingTierMigrationStatus(): Promise<{ success: boolean; status?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/migration-status`, {
        headers: withBearerAuth(),
      })
      const data = await response.json()
      return { success: response.ok, status: data.status, error: data.error }
    } catch (error: any) {
      console.error('Error getting migration status:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Migrate company billing tiers
   */
  async migrateCompanyBillingTiers(dryRun: boolean = true): Promise<{ success: boolean; results?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/migrate-billing-tiers`, {
        method: 'POST',
        headers: withBearerAuth({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ dry_run: dryRun })
      })
      const data = await response.json()
      return { success: response.ok, results: data.results, error: data.error }
    } catch (error: any) {
      console.error('Error migrating billing tiers:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Create billing tier
   */
  async createBillingTier(tierData: Partial<BillingTier>): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-tiers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tierData)
      })
      const data = await response.json()
      return { success: response.ok, data, error: data.error }
    } catch (error: any) {
      console.error('Error creating billing tier:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Update billing tier
   */
  async updateBillingTier(tierId: string, updates: Partial<BillingTier>): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-tiers/${tierId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      })
      const data = await response.json()
      return { success: response.ok, data, error: data.error }
    } catch (error: any) {
      console.error('Error updating billing tier:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Initialize billing tiers
   */
  async initializeBillingTiers(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/initialize-billing-tiers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      return { success: response.ok, data, error: data.error }
    } catch (error: any) {
      console.error('Error initializing billing tiers:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get affected companies count for a billing tier
   * Returns information about how many companies are using a specific tier
   */
  async getAffectedCompaniesForTier(tierId: string): Promise<{
    success: boolean
    tier_id?: string
    tier_key?: string
    tier_name?: string
    affected_count?: {
      billing_accounts: number
      legacy_companies: number
      total: number
    }
    affected_companies?: Array<{ id: string; name: string }>
    can_delete_safely?: boolean
    error?: string
  }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-tiers/${tierId}/affected-companies`)
      const data = await response.json()
      return { success: response.ok, ...data }
    } catch (error: any) {
      console.error('Error getting affected companies for tier:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Delete billing tier with mandatory reassignment
   * Deletes a billing tier and reassigns all affected companies to a replacement tier
   * Also sends notification emails to company managers/admins
   */
  async deleteBillingTierWithReassignment(
    tierId: string,
    replacementTierId: string | null,
    adminUserId?: string
  ): Promise<{
    success: boolean
    message?: string
    audit?: {
      deleted_tier: {
        id: string
        name: string
        tier_key: string
      }
      replacement_tier?: {
        id: string
        name: string
        tier_key: string
      }
      affected_companies: number
      email_notifications?: {
        total: number
        successful: number
        failed: number
      }
    }
    error?: string
  }> {
    try {
      const body: Record<string, any> = {}
      if (replacementTierId) {
        body.replacement_tier_id = replacementTierId
      }
      if (adminUserId) {
        body.admin_user_id = adminUserId
      }

      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-tiers/${tierId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      const data = await response.json()
      return { success: response.ok, ...data }
    } catch (error: any) {
      console.error('Error deleting billing tier:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================================================
  // SERVER ENDPOINT WRAPPER METHODS
  // ============================================================================

  /**
   * Get billing tiers via server (wrapper method)
   */
  async getBillingTiersViaServer(activeOnly: boolean = true): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.getBillingTiers(activeOnly)
  }

  /**
   * Get Stripe config via server (wrapper method)
   */
  async getStripeConfigViaServer(): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.getStripeConfig()
  }

  /**
   * Get subscription counts via server (wrapper method)
   */
  async getSubscriptionCountsViaServer(): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.getSubscriptionCounts()
  }

  /**
   * Get test data stats via server (wrapper method)
   */
  async getTestDataStatsViaServer(): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.getTestDataStats()
  }

  /**
   * Create test customers via server (wrapper method)
   */
  async createTestCustomersViaServer(count: number): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.createTestCustomers(count)
  }

  /**
   * Cleanup test customers via server (wrapper method)
   */
  async cleanupTestCustomersViaServer(): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.cleanupTestCustomers()
  }

  /**
   * Sync individual tier via server (wrapper method)
   */
  async syncIndividualTierViaServer(tierId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.syncIndividualTier(tierId)
  }

  /**
   * Sync Stripe catalog via server (wrapper method)
   */
  async syncStripeCatalogViaServer(): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.syncStripeCatalog()
  }

  /**
   * Create checkout session via server (wrapper method)
   */
  async createCheckoutSessionViaServer(tierId: string, billingCycle: string, userCount: number): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.createSubscriptionCheckout({
      tier_id: tierId,
      billing_cycle: billingCycle as 'monthly' | 'yearly',
      user_count: userCount,
      company_id: 'test-company',
      user_id: 'test-user'
    })
  }

  /**
   * Open Stripe portal via server (wrapper method)
   */
  async openStripePortalViaServer(): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.createCustomerPortalSession({
      customer_id: 'test-customer',
      return_url: window.location.origin
    })
  }

  // ============================================================================
  // BILLING OVERRIDE METHODS
  // ============================================================================

  /**
   * Get company billing override
   */
  async getCompanyBillingOverride(companyId: string): Promise<{ success: boolean; override?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-override/${companyId}`, {
        headers: withBearerAuth(),
      })
      const data = await response.json()
      return { success: response.ok, override: data.override, error: data.error }
    } catch (error: any) {
      console.error('Error getting company billing override:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Create company billing override
   */
  async createCompanyBillingOverride(overrideData: any): Promise<{ success: boolean; override?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-override`, {
        method: 'POST',
        headers: withBearerAuth({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(overrideData)
      })
      const data = await response.json()
      return { success: response.ok, override: data.override, error: data.error }
    } catch (error: any) {
      console.error('Error creating company billing override:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Update company billing override
   */
  async updateCompanyBillingOverride(companyId: string, updates: any): Promise<{ success: boolean; override?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-override/${companyId}`, {
        method: 'PUT',
        headers: withBearerAuth({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(updates)
      })
      const data = await response.json()
      return { success: response.ok, override: data.override, error: data.error }
    } catch (error: any) {
      console.error('Error updating company billing override:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Delete company billing override
   */
  async deleteCompanyBillingOverride(companyId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-override/${companyId}`, {
        method: 'DELETE',
        headers: withBearerAuth(),
      })
      const data = await response.json()
      return { success: response.ok, error: data.error }
    } catch (error: any) {
      console.error('Error deleting company billing override:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get all company billing overrides (admin only)
   */
  async getAllCompanyBillingOverrides(): Promise<{ success: boolean; overrides?: any[]; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-overrides`, {
        headers: withBearerAuth(),
      })
      const data = await response.json()
      return { success: response.ok, overrides: data.overrides, error: data.error }
    } catch (error: any) {
      console.error('Error getting all company billing overrides:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Calculate company billing with override
   */
  async calculateCompanyBillingWithOverride(companyId: string, userCount?: number): Promise<{ success: boolean; billing?: any; error?: string }> {
    try {
      const params = new URLSearchParams()
      if (userCount !== undefined) {
        params.append('user_count', userCount.toString())
      }

      const response = await fetch(`${this.serverBaseUrl}/api/stripe/billing-calculation/${companyId}?${params}`, {
        headers: withBearerAuth(),
      })
      const data = await response.json()
      return { success: response.ok, billing: data.billing, error: data.error }
    } catch (error: any) {
      console.error('Error calculating company billing with override:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Create trial subscription for a company
   */
  async createTrialSubscription(params: {
    company_id: string
    tier_id: string
    user_count: number
    trial_days: number
    billing_cycle?: 'monthly' | 'yearly'
    customer_email?: string
    customer_name?: string
  }): Promise<{ success: boolean; subscription?: any; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/trial-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })
      const data = await response.json()
      return { success: response.ok, subscription: data.subscription, error: data.error }
    } catch (error: any) {
      console.error('Error creating trial subscription:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Sync billing account with Stripe data
   */
  async syncBillingAccount(companyId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/sync-billing/${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      return { success: response.ok, message: data.message, error: data.error }
    } catch (error: any) {
      console.error('Error syncing billing account:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get subscription and trial history for a company
   */
  async getSubscriptionHistory(companyId: string, limit: number = 50): Promise<{
    success: boolean;
    history?: any[];
    total_count?: number;
    error?: string
  }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/subscription-history/${companyId}?limit=${limit}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      return {
        success: response.ok,
        history: data.history,
        total_count: data.total_count,
        error: data.error
      }
    } catch (error: any) {
      console.error('Error getting subscription history:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Download invoice PDF
   */
  async downloadInvoice(invoiceId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/download-invoice/${invoiceId}`, {
        method: 'GET'
      })

      if (response.ok) {
        // Get the PDF blob
        const blob = await response.blob()

        // Create download link
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `invoice-${invoiceId}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        return { success: true }
      } else {
        const errorData = await response.json()
        return { success: false, error: errorData.error || 'Failed to download invoice' }
      }
    } catch (error: any) {
      console.error('Error downloading invoice:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Create Stripe customer for a company
   */
  async createCustomer(companyId: string, customerData: {
    name?: string
    email?: string
    phone?: string
    address?: {
      line1?: string
      line2?: string
      city?: string
      state?: string
      postal_code?: string
      country?: string
    }
    tax_ids?: Array<{
      type: string
      value: string
    }>
    metadata?: Record<string, string>
  }): Promise<{
    success: boolean;
    billing_info?: any;
    message?: string;
    error?: string
  }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/customer-billing/${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData)
      })
      const data = await response.json()
      return {
        success: response.ok,
        billing_info: data.billing_info,
        message: data.message,
        error: data.error
      }
    } catch (error: any) {
      console.error('Error creating customer:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get customer billing information
   */
  async getCustomerBillingInfo(companyId: string): Promise<{
    success: boolean;
    billing_info?: any;
    error?: string
  }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/customer-billing/${companyId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      const data = await response.json()
      return {
        success: response.ok,
        billing_info: data.billing_info,
        error: data.error
      }
    } catch (error: any) {
      console.error('Error getting customer billing info:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Update customer billing information
   */
  async updateCustomerBillingInfo(companyId: string, billingInfo: {
    name?: string
    email?: string
    phone?: string
    address?: {
      line1?: string
      line2?: string
      city?: string
      state?: string
      postal_code?: string
      country?: string
    }
    tax_ids?: Array<{
      type: string
      value: string
    }>
    metadata?: Record<string, string>
  }): Promise<{
    success: boolean;
    billing_info?: any;
    message?: string;
    error?: string
  }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/customer-billing/${companyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(billingInfo)
      })
      const data = await response.json()
      return {
        success: response.ok,
        billing_info: data.billing_info,
        message: data.message,
        error: data.error
      }
    } catch (error: any) {
      console.error('Error updating customer billing info:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Verify VAT number using Stripe's validation service
   */
  async verifyVatNumber(vatNumber: string): Promise<{
    success: boolean
    valid?: boolean
    error?: string
    details?: any
  }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/verify-vat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vat_number: vatNumber })
      })
      const data = await response.json()
      return {
        success: response.ok,
        valid: data.valid,
        details: data.details,
        error: data.error
      }
    } catch (error: any) {
      console.error('Error verifying VAT number:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Create a tax ID for a customer
   */
  async createCustomerTaxId(companyId: string, vatNumber: string): Promise<{
    success: boolean
    tax_id?: any
    error?: string
  }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/customer-tax-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          vat_number: vatNumber
        })
      })
      const data = await response.json()
      return {
        success: response.ok,
        tax_id: data.tax_id,
        error: data.error
      }
    } catch (error: any) {
      console.error('Error creating customer tax ID:', error)
      return { success: false, error: error.message }
    }
  }
}

export const billingService = new BillingService()
export default billingService